import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/services/api.js';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

/**
 * Citizen self-signup. NACIS identities are phone-first, so phone is
 * required; email is the verification channel (used to issue the
 * 6-digit OTP that proves email ownership). On success we navigate to
 * /verify-otp with the email + dev_otp threaded through router state.
 */
export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirm: '',
  });
  const [errors,    setErrors]    = useState({});
  const [submit,    setSubmit]    = useState(false);
  const [serverErr, setServerErr] = useState(null);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
    if (serverErr) setServerErr(null);
  }

  function validate() {
    const e = {};
    if (!form.phone.trim()) e.phone = 'Phone is required.';
    else if (!/^[+\d][\d\s\-()]{6,}$/.test(form.phone)) e.phone = 'Enter a valid phone number.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.password) e.password = 'Password is required.';
    else if (form.password.length < 10) e.password = 'Password must be at least 10 characters.';
    else if (!/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/\d/.test(form.password)) {
      e.password = 'Password must include upper, lower, and a number.';
    }
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmit(true);
    setServerErr(null);
    try {
      const res = await authApi.register({
        name:     form.name.trim() || null,
        phone:    form.phone.trim(),
        email:    form.email.trim(),
        password: form.password,
        mode:     'identified',
      });

      navigate('/verify-otp', {
        replace: true,
        state: {
          email:  res?.email || form.email.trim(),
          devOtp: res?.dev_otp || null,
          from:   'register',
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      if (status === 422 && data.errors) {
        // Laravel validation errors — surface the first message per field.
        const field = Object.keys(data.errors)[0];
        setServerErr(data.errors[field]?.[0] || data.message || 'Please check the form.');
      } else if (!err?.response) {
        setServerErr('Could not reach the NACIS API. Check your connection and try again.');
      } else {
        setServerErr(data.message || 'Sign-up failed. Please try again.');
      }
    } finally {
      setSubmit(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: brand panel */}
      <aside className="relative hidden bg-slate-950 lg:flex lg:flex-col lg:p-12 lg:text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px circle at 0% 0%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(800px circle at 100% 100%, rgba(14,165,233,0.18), transparent 45%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-1 flex-col">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
              <Shield className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-wide">{t('brand.name')}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{t('brand.full')}</div>
            </div>
          </Link>

          <div className="mt-auto max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">Citizen sign-up</div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight">
              Report safely, anywhere — verified once, protected always.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              NACIS identities are phone-first and email-verified. We use your email
              only to issue a 6-digit code that proves you own it. You can submit
              reports anonymously after that — the binding is for safety, not surveillance.
            </p>
          </div>
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="relative flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="absolute right-4 top-4 lg:right-8 lg:top-8">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-2 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
              <Shield className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="text-sm font-bold tracking-wide text-slate-900">{t('brand.name')}</div>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Create your account</h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Citizen self-signup. We'll email a 6-digit verification code.
          </p>

          {serverErr && (
            <div role="alert" className="mt-6 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{serverErr}</div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-800">Full name <span className="text-slate-400">(optional)</span></label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-800">Phone</label>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                placeholder="+1 555 555 5555"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.phone && <div className="mt-1 text-xs text-rose-600">{errors.phone}</div>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-800">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.email && <div className="mt-1 text-xs text-rose-600">{errors.email}</div>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-800">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={e => update('password', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.password && <div className="mt-1 text-xs text-rose-600">{errors.password}</div>}
              <div className="mt-1 text-xs text-slate-500">Min 10 chars, upper + lower + number.</div>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-slate-800">Confirm password</label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={form.confirm}
                onChange={e => update('confirm', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.confirm && <div className="mt-1 text-xs text-rose-600">{errors.confirm}</div>}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submit}>
              {submit ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : 'Create account'}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-sky-700 hover:text-sky-800">
              Sign in
            </Link>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-6 text-xs text-slate-500">
            By creating an account you agree to NACIS' Terms and acknowledge the Privacy Notice.
          </div>
        </div>
      </main>
    </div>
  );
}
